import React from "react";
import { getFulcrumPhotoAsBlob, isFulcrumPhotoUrl, FulcrumRegion, detectFulcrumRegion } from "../utilities/fulcrumLibService";
import { useAuthSelector } from "../store/auth";

/**
 * React hook to fetch and manage authenticated Fulcrum photos
 *
 * @param photoUrl The full photo URL or just the photo ID
 * @param region The Fulcrum region (US, AU, EU)
 * @param thumbnail Whether to get thumbnail or full size
 */
export const useFulcrumPhoto = (photoUrl: string | null | undefined, region: FulcrumRegion = "AU", thumbnail: boolean = true) => {
  const [blobUrl, setBlobUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const { user: profile } = useAuthSelector();

  React.useEffect(() => {
    // Reset state
    setBlobUrl(null);
    setError(null);

    if (!photoUrl) {
      return;
    }

    // If it's not a Fulcrum photo URL, just use it directly
    if (!isFulcrumPhotoUrl(photoUrl)) {
      setBlobUrl(photoUrl);
      setLoading(false);
      return;
    }

    // Extract photo ID from URL if needed
    let photoId = photoUrl;
    // Handle both URL patterns:
    // - /api/v2/photos/PHOTO_ID/thumbnail.jpg (from constructFulcrumPhotoUrl)
    // - /photos/PHOTO_ID/thumbnail.jpg (from getFulcrumPhotoUrl)
    const photoIdMatch = photoUrl.match(/\/(?:api\/v2\/)?photos\/([^\/]+)(?:\/thumbnail)?\.jpg/);
    if (photoIdMatch) {
      photoId = photoIdMatch[1];
    }

    setLoading(true);

    // Enhanced Fulcrum photo loading with multiple fallback strategies
    const loadFulcrumPhotoWithFallbacks = async () => {
      const errorMessage = (err: any) => (err instanceof Error ? err.message : String(err));

      try {
        // Strategy 1: Try with provided region
        const blobUrl = await getFulcrumPhotoAsBlob(photoId, profile, thumbnail);
        setBlobUrl(blobUrl);
        setError(null);
        return;
      } catch (error1) {
        const msg1 = errorMessage(error1);

        // Strategy 2: Try auto-detecting region from photo ID or form context
        if (region !== "AU") {
          try {
            const blobUrl = await getFulcrumPhotoAsBlob(photoId, profile, thumbnail, "AU");
            setBlobUrl(blobUrl);
            setError(null);
            return;
          } catch (error2) {}
        }

        // Strategy 3: Try US region
        if (region !== "US") {
          try {
            const blobUrl = await getFulcrumPhotoAsBlob(photoId, profile, thumbnail, "US");
            setBlobUrl(blobUrl);
            setError(null);
            return;
          } catch (error3) {}
        }

        // Strategy 4: Try without thumbnail (full image) in case thumbnail is broken
        if (thumbnail) {
          try {
            const blobUrl = await getFulcrumPhotoAsBlob(photoId, profile, false, region);
            setBlobUrl(blobUrl);
            setError(null);
            return;
          } catch (error4) {}
        }

        // Strategy 5: Check if this is a cached media URL fallback
        try {
          const { getCachedData } = await import("../utilities/relatedItemsCache");
          const cachedUrl = getCachedData<string>(`photo_fallback_${photoId}`, "media");
          if (cachedUrl) {
            setBlobUrl(cachedUrl);
            setError(null);
            return;
          }
        } catch (cacheError) {}

        // All strategies failed - provide detailed error info
        const is403 = msg1.includes("403") || msg1.includes("Forbidden");
        const isNetwork = msg1.includes("NetworkError") || msg1.includes("fetch");
        const isInvalidKey = msg1.includes("API key") || msg1.includes("authentication");

        let userFriendlyError = "Unable to load photo from the external data source";
        if (is403) {
          userFriendlyError = "Photo access denied - please check your API key";
        } else if (isNetwork) {
          userFriendlyError = "Network error loading photo - please check your connection";
        } else if (isInvalidKey) {
          userFriendlyError = "Invalid API key - please update your settings";
        }

        console.error(`[useFulcrumPhoto] All fallback strategies failed for photo ${photoId}:`, msg1);
        setError(userFriendlyError);
        setBlobUrl("/icons/image-placeholder.svg");
      }
    };

    loadFulcrumPhotoWithFallbacks().finally(() => {
      setLoading(false);
    });
  }, [photoUrl, region, thumbnail, profile]);

  return { blobUrl, loading, error };
};
